import { useCallback, useState } from 'react';
import { PostId } from '../model/post.types';
import { Reply, ReplyId } from '../model/reply.types';
import { threadParentId } from './groupReplies';
import { analytics } from './analytics';

// Owns the reply-thread interaction state: which reply the composer targets and
// which threads are expanded. Pure state only - scroll/focus side effects and
// submission stay in the component that holds the DOM refs.
export const useReplyThread = (postId: PostId) => {
  const [replyTarget, setReplyTarget] = useState<Reply | null>(null);
  const [expandedThreads, setExpandedThreads] = useState<ReadonlySet<ReplyId>>(
    new Set(),
  );

  const expandThread = useCallback(
    (id: ReplyId) => setExpandedThreads((current) => new Set(current).add(id)),
    [],
  );

  const toggleThread = useCallback(
    (id: ReplyId) => {
      if (!expandedThreads.has(id)) {
        analytics.trackReplyThreadExpanded();
      }
      setExpandedThreads((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [expandedThreads],
  );

  const startReply = useCallback(
    (target: Reply) => {
      setReplyTarget(target);
      expandThread(threadParentId(target, postId));
    },
    [expandThread, postId],
  );

  const clearReplyTarget = useCallback(() => setReplyTarget(null), []);

  return {
    replyTarget,
    expandedThreads,
    expandThread,
    toggleThread,
    startReply,
    clearReplyTarget,
  };
};
